'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { RefreshCw, CheckCircle, ChevronDown, Save, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import type { EditorPost } from './ContentEditorLayout';

interface Props {
  post: EditorPost;
  onUpdate: (content: string) => void;
  onStatusChange: (status: string) => void;
  onSave?: (post: EditorPost, content: string) => Promise<void>;
}

const statusOptions = ['Entwurf', 'KI-generiert', 'Bearbeitet', 'Freigegeben', 'Geplant', 'Veröffentlicht'];

const platformLimits: Record<string, number> = {
  LI: 3000,
  IG: 2200,
  X: 280,
  FB: 63206,
  BLOG: 99999,
};

interface FormData {
  content: string;
}

export default function PostEditor({ post, onUpdate, onStatusChange, onSave }: Props) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const { register, handleSubmit, watch, reset, formState: { isDirty } } = useForm<FormData>({
    defaultValues: { content: post.content },
  });

  useEffect(() => {
    reset({ content: post.content });
  }, [post.id, reset, post.content]);

  const contentValue = watch('content');
  const charCount = contentValue?.length ?? 0;
  const limit = platformLimits[post.platform] ?? 3000;
  const pct = Math.min((charCount / limit) * 100, 100);
  const overLimit = charCount > limit;

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      onUpdate(data.content);
      if (onSave) {
        await onSave(post, data.content);
      }
      toast.success('Beitrag gespeichert');
      reset({ content: data.content });
    } catch (err: any) {
      toast.error(err?.message ?? 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = () => {
    setRegenerating(true);
    // TODO: Backend — POST /api/posts/:id/regenerate to get new AI draft
    setTimeout(() => {
      setRegenerating(false);
      toast.success('Neuer KI-Entwurf generiert');
    }, 1800);
  };

  const handleDownload = () => {
    const filename = `${post.platform}_${post.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    const text = `Plattform: ${post.platform}\nFormat: ${post.format}\nTitel: ${post.title}\nTon: ${post.tone}\nStatus: ${post.status}\n\n---\n\n${contentValue}`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Beitrag heruntergeladen');
  };

  const platformBadge: Record<string, string> = {
    LI: 'platform-badge-li',
    IG: 'platform-badge-ig',
    X: 'platform-badge-x',
    FB: 'platform-badge-fb',
    BLOG: 'platform-badge-blog',
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex-1 flex flex-col border-r border-border min-w-0 bg-black"
    >
      {/* Editor Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`status-badge ${platformBadge[post.platform] ?? ''}`}>
            {post.platform}
          </span>
          <span className="text-xs text-muted truncate">{post.format}</span>
          <span className="text-muted">·</span>
          <span className="text-xs text-muted mono">Tag {post.day}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDirty && (
            <span className="text-xs mono text-yellow-400 px-2 py-0.5 bg-yellow-950/20 border border-yellow-800/30 rounded">
              Ungespeichert
            </span>
          )}
          {/* Status dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setStatusOpen(!statusOpen)}
              className="btn-ghost py-1.5 px-3 text-xs gap-1.5"
            >
              {post.status}
              <ChevronDown size={11} />
            </button>
            {statusOpen && (
              <div className="absolute right-0 top-full mt-1 bg-surface border border-border rounded shadow-glow z-20 min-w-[140px]">
                {statusOptions.map((s) => (
                  <button
                    key={`status-opt-${s}`}
                    type="button"
                    onClick={() => { onStatusChange(s); setStatusOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-2 transition-colors ${
                      post.status === s ? 'text-accent' : 'text-white'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="btn-ghost py-1.5 px-3 text-xs gap-1.5"
          >
            {regenerating ? (
              <RefreshCw size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {regenerating ? 'Generiert…' : 'Neu generieren'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="btn-ghost py-1.5 px-3 text-xs gap-1.5"
            title="Beitrag herunterladen"
          >
            <Download size={12} />
            Download
          </button>
          <button
            type="submit"
            disabled={saving || !isDirty}
            className="btn-primary py-1.5 px-3 text-xs gap-1.5"
          >
            {saving ? (
              <RefreshCw size={12} className="animate-spin" />
            ) : (
              <Save size={12} />
            )}
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="px-5 pt-4 pb-2 border-b border-border flex-shrink-0">
        <h2 className="text-base font-700 text-white leading-snug">{post.title}</h2>
        <div className="flex gap-4 mt-1.5 text-xs mono text-muted">
          <span>Ton: <span className="text-accent">{post.tone}</span></span>
          <span>{post.words} Wörter</span>
          <span className={overLimit ? 'text-red-400' : ''}>{charCount}/{limit} Zeichen</span>
        </div>
      </div>

      {/* Char limit bar */}
      <div className="px-5 py-2 flex-shrink-0">
        <div className="h-0.5 bg-surface-2 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-200 ${
              overLimit ? 'bg-red-500' : pct > 80 ? 'bg-yellow-500' : 'bg-accent'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {overLimit && (
          <p className="text-xs text-red-400 mono mt-1">
            {charCount - limit} Zeichen über dem Limit für {post.platform}
          </p>
        )}
      </div>

      {/* Textarea */}
      <div className="flex-1 px-5 pb-5 min-h-0">
        <textarea
          className="w-full h-full bg-transparent text-sm text-white leading-relaxed outline-none resize-none font-sans placeholder-muted-2"
          placeholder="Beitragsinhalt bearbeiten…"
          {...register('content')}
        />
      </div>

      {/* Bottom bar */}
      <div className="px-5 py-3 border-t border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <CheckCircle size={13} className="text-green-400" />
          <span className="text-xs text-muted">Automatisch als KI-generiert markiert</span>
        </div>
        <span className="mono text-xs text-muted">
          Zuletzt gespeichert: 02.04.2026 · 14:32
        </span>
      </div>
    </form>
  );
}